import { useMutation } from "@tanstack/react-query";
import { useId, useState, type FormEvent, type Ref } from "react";
import { domainError, errorMessage, useTRPC } from "../../lib/api";
import { IconButton } from "../../ui/icons";
import { formatDue, previewQuickAdd, toCommand, type QuickAddPreview } from "./model";

const EXTRA_REASONS: Record<string, string> = {
  unknown_assignee: "Nobody in this workspace matches that @name.",
  personal_is_private: "Tasks in Personal are always private.",
  task_command: "That is not a task yet: type a title.",
};

function quickAddError(error: unknown): string {
  const reason = domainError(error)?.reason;
  const known = reason === undefined ? undefined : EXTRA_REASONS[reason];
  return known === undefined ? errorMessage(error) : known;
}

/**
 * One line, `/task` syntax with the prefix optional: `@person title by Friday #private`. Shows
 * what will be created while typing; the api parses it again and resolves the @name.
 */
export function QuickAdd({
  workspaceId,
  personal,
  inputRef,
  now,
}: {
  workspaceId: string;
  personal: boolean;
  inputRef?: Ref<HTMLInputElement>;
  now: Date;
}) {
  const trpc = useTRPC();
  const [text, setText] = useState("");
  const [added, setAdded] = useState<string | null>(null);
  const id = useId();
  const create = useMutation(
    trpc.tasks.fromCommand.mutationOptions({
      onSuccess: (task) => {
        setAdded(task.title);
        setText("");
      },
    }),
  );
  const preview = previewQuickAdd(text, now);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (preview === null || !preview.ok) return;
    setAdded(null);
    create.mutate({ workspaceId, text: toCommand(text) });
  }

  return (
    <form onSubmit={submit} aria-label="Quick add task" className="flex flex-col gap-1">
      <label htmlFor={id} className="sr-only">
        Quick add a task
      </label>
      <div className="flex items-center gap-2 rounded-xl border border-border bg-surface py-1 pl-1 pr-1.5">
        <input
          id={id}
          ref={inputRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            create.reset();
          }}
          placeholder={personal ? "Add a task: Buy stamps by Friday" : "Add a task: @jordyn Send the quote by Friday #private"}
          aria-describedby={`${id}-feedback`}
          aria-keyshortcuts="n"
          autoComplete="off"
          className="min-h-10 min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-text outline-none placeholder:text-muted"
        />
        <IconButton
          type="submit"
          icon="send"
          tone="primary"
          label="Add task"
          className="size-8"
          disabled={create.isPending || preview === null || !preview.ok}
        />
      </div>
      {/* The preview is described, not announced, so typing stays quiet; outcomes are announced. */}
      <p role="status" className="sr-only">
        {added === null ? "" : `Added task ${added}.`}
      </p>
      <div id={`${id}-feedback`} className="min-h-5 break-words px-3 text-xs">
        <Feedback
          error={create.isError ? quickAddError(create.error) : null}
          preview={preview}
          added={added}
          personal={personal}
          now={now}
        />
      </div>
    </form>
  );
}

function Feedback({
  error,
  preview,
  added,
  personal,
  now,
}: {
  error: string | null;
  preview: QuickAddPreview;
  added: string | null;
  personal: boolean;
  now: Date;
}) {
  if (error !== null) {
    return (
      <span role="alert" className="text-danger">
        {error}
      </span>
    );
  }
  if (preview === null) {
    return (
      <span className="text-muted">
        {added === null ? "Type a task. Optional: @name, “by Friday”, #private." : `Added “${added}”.`}
      </span>
    );
  }
  if (!preview.ok) {
    return <span className="text-danger">{preview.error === "a task needs a title" ? "A task needs a title." : preview.error}</span>;
  }
  const { title, assigneeHandle, due, visibility } = preview.command;
  return (
    <span className="text-muted">
      Will add <strong className="font-medium text-text">“{title}”</strong>
      {assigneeHandle === null ? null : <> for @{assigneeHandle}</>}
      {due === null ? null : <>, due {formatDue(due, now)}</>}
      {personal || visibility === "private" ? ", private" : ", shared with the workspace"}
    </span>
  );
}
